'use client';

import { useEffect } from 'react';

import { useRouter } from 'next/navigation';

import { useQueryClient } from '@tanstack/react-query';

import {
  DATA_CHANGED_EVENT,
  SMART_UPLOAD_EVENT,
  notifyDataChanged,
} from './data-refresh-events';
import { describeRequest, isMutatingRequest } from './is-mutating-request';

/**
 * Keeps every screen current after a write, without a page reload.
 *
 * ## The problem
 *
 * Deleting an order and clicking through to the dashboard used to show the
 * order still sitting there; only a hard refresh cleared it. Nothing was wrong
 * with the delete. The dashboard reads its orders through React Query, the
 * default `staleTime` held that answer for a minute, and a client-side
 * navigation inside that minute is served from the cache without asking the
 * server. The same shape of bug has been patched one page at a time before —
 * the orders list grew a window-focus refetch, Smart Upload grew its own
 * broadcast event — which is the sign it wants solving once, centrally.
 *
 * ## The approach
 *
 * A write is a write no matter which button started it, and they all leave
 * through `fetch`: route handlers, server actions and the browser Supabase
 * client alike. So this wraps `fetch` once, and when one of those calls
 * succeeds it does the two things that make stale data impossible:
 *
 *   1. `invalidateQueries()` — mounted React Query views refetch immediately,
 *      unmounted ones are marked stale and refetch when next opened.
 *   2. `router.refresh()` — server components re-render, which covers the
 *      layout, the sidebar counts and anything else rendered on the server.
 *
 * Screens holding rows in their own `useState` opt in with `useDataRefresh`.
 *
 * ## Why not just invalidate at each call site
 *
 * That is the version we already had, and the dashboard bug is what it looks
 * like when one call site is missed. There are dozens of writers and every new
 * one would have to remember. Deciding centrally from the request itself means
 * a new mutation is covered the day it is written.
 *
 * The cost is some refetching that a hand-written invalidation would have
 * skipped. That is the right trade here: these are the same GETs the page
 * makes on mount, only mounted queries actually run, and bursts are coalesced.
 */

/** Long enough to fold a burst of writes into one wave, short enough to feel immediate. */
const COALESCE_MS = 150;

const PATCHED_FLAG = '__supplyAutomateFetchPatched';

type PatchableWindow = typeof globalThis & { [PATCHED_FLAG]?: true };

/**
 * Wrap `fetch` so successful writes announce themselves.
 *
 * Installed at module scope on the client rather than in an effect: an effect
 * runs after the first paint, and a write fired from an eagerly-mounted
 * component could slip through before the patch was in place. The flag makes
 * it idempotent under React strict mode's double render and fast refresh.
 */
function installFetchInterceptor() {
  if (typeof window === 'undefined') return;

  const patchable = window as PatchableWindow;

  if (patchable[PATCHED_FLAG]) return;

  patchable[PATCHED_FLAG] = true;

  const originalFetch = window.fetch.bind(window);
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;

  window.fetch = async function patchedFetch(input, init) {
    const response = await originalFetch(input, init);

    /*
     * Bookkeeping must never be able to fail a request the app is waiting on,
     * so everything after the response is caught and dropped.
     */
    try {
      if (response.ok) {
        const facts = describeRequest(input, init, window.location.origin);

        if (
          facts &&
          isMutatingRequest(facts, window.location.origin, supabaseUrl)
        ) {
          notifyDataChanged();
        }
      }
    } catch {
      // ignored on purpose
    }

    return response;
  };
}

installFetchInterceptor();

export function DataRefreshProvider(props: React.PropsWithChildren) {
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    function refresh() {
      timer = null;

      /*
       * No filter, so this covers keys we have not heard of. React Query only
       * refetches the queries currently mounted; the rest are flagged stale
       * and re-read the next time their screen opens.
       */
      void queryClient.invalidateQueries();
      router.refresh();
    }

    function schedule() {
      if (timer) clearTimeout(timer);

      timer = setTimeout(refresh, COALESCE_MS);
    }

    window.addEventListener(DATA_CHANGED_EVENT, schedule);
    window.addEventListener(SMART_UPLOAD_EVENT, schedule);

    return () => {
      if (timer) clearTimeout(timer);

      window.removeEventListener(DATA_CHANGED_EVENT, schedule);
      window.removeEventListener(SMART_UPLOAD_EVENT, schedule);
    };
  }, [queryClient, router]);

  return props.children;
}
